// RelatedProducts.js
import React, { useEffect, useState } from 'react';
import apiClient from '../../api/apiClient';
import ContentCard from './ContentCard';

const SkeletonCard = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
    <div className="pb-[100%] bg-gray-200"></div>
    <div className="p-3 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
    </div>
  </div>
);

const RelatedProducts = ({ categorySlug, subcategorySlug, slug }) => {
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!slug) {
      setLoading(false);
      return;
    }

    const fetchRelated = async () => {
      setLoading(true);
      setError(null);

      try {
        let results = [];

        // اگر دسته‌بندی داریم، از endpoint قدیمی استفاده کن
        if (categorySlug) {
          let url;
          if (subcategorySlug) {
            url = `/products/${encodeURIComponent(categorySlug)}/${encodeURIComponent(subcategorySlug)}/${encodeURIComponent(slug)}/related/`;
          } else {
            url = `/products/${encodeURIComponent(categorySlug)}/${encodeURIComponent(slug)}/related/`;
          }
          const finalUrl = `${url}?limit=8`;
          console.log(`Fetching related products (category-based): ${finalUrl}`);
          const response = await apiClient.get(finalUrl, { signal });
          results = response.data || [];
        } else {
          // بدون دسته‌بندی: بر اساس تگ‌های مشترک جستجو کن
          // 1. ابتدا اطلاعات محصول فعلی را بگیر تا تگ‌هایش را بدست آوری
          const productRes = await apiClient.get(`/products/${encodeURIComponent(slug)}/`, { signal });
          const product = productRes.data;
          const tagSlugs = (product.tags || []).map(tag => tag.slug);

          if (tagSlugs.length === 0) {
            // اگر تگی ندارد، هیچ محصول مرتبطی نیست
            results = [];
          } else {
            // 2. برای هر تگ، محصولات دیگر را بگیر و جمع کن (بدون تکرار و بدون محصول فعلی)
            const promises = tagSlugs.map(tagSlug =>
              apiClient.get(`/products/list/?tag=${encodeURIComponent(tagSlug)}&limit=8`, { signal })
            );
            const responses = await Promise.all(promises);
            // ترکیب نتایج و حذف موارد تکراری و محصول فعلی
            const combined = responses.flatMap(res => res.data.results || res.data || []);
            const unique = new Map();
            for (const item of combined) {
              if (item.slug !== slug && !unique.has(item.slug)) {
                unique.set(item.slug, item);
              }
            }
            results = Array.from(unique.values()).slice(0, 8);
          }
        }

        setRelated(results);
      } catch (err) {
        if (err.name === 'CanceledError') return;
        console.error('Error fetching related products:', err);
        setError('خطا در بارگذاری محتوای مرتبط.');
      } finally {
        setLoading(false);
      }
    };

    fetchRelated();
    return () => controller.abort();
  }, [categorySlug, subcategorySlug, slug]);

  if (error) {
    return (
      <section className="mb-8 px-4 text-center text-red-600">
        <p>{error}</p>
      </section>
    );
  }

  if (!loading && related.length === 0) return null;

  return (
    <section className="mb-0.5 mt-4 px-1 rtl w-full max-w-[460px] mx-auto">
      <h2 className="text-m font-extrabold text-gray-500 mb-1">
        مـحتـوای مرتـبط
      </h2>
      <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        {loading
          ? Array(8).fill(0).map((_, i) => <SkeletonCard key={`sk-${i}`} />)
          : related.map((item) => (
              <ContentCard
                key={item.id || item.slug}
                data={{
                  ...item,
                  productUrl: item.product_url || `/products/${item.slug}`,
                  cover_url: item.cover_url || null,
                }}
              />
            ))
        }
      </div>
    </section>
  );
};

export default RelatedProducts;